/**
 * Client-side telemetry — thin fire-and-forget wrapper around the
 * backend `POST /api/debug/client-event` endpoint. Never blocks the
 * UI, never surfaces errors: telemetry loss is preferable to
 * degrading the sign-in experience.
 *
 * See /app/backend/server.py `post_client_debug_event` for the
 * whitelist of accepted `event_type` values and rate-limit rules.
 */
// Same-origin fallback when REACT_APP_BACKEND_URL is unset — matches
// the pattern in `App.js` (`const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;`).
// On unified Railway deployments the Docker build intentionally leaves
// REACT_APP_BACKEND_URL empty so bundled axios calls become relative
// `/api/*` URLs. Without this fallback, every `reportClientEvent()`
// call silently no-op'd on Railway — cost us a full night of missing
// passkey-failure telemetry 2026-09-20. The `typeof window` guard keeps
// this file safe to import from a SSR context (unused today but future-
// proofs it).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
  || (typeof window !== 'undefined' ? window.location.origin : '');

const ALLOWED_EVENTS = new Set([
  'client_session_start',
  'passkey_auth_failed',
  'passkey_register_failed',
  'passkey_flow_initiated',
  'queue_fetch_timing',
  'in_app_browser_blocked',
  'in_app_browser_dismissed',
]);

/**
 * Client session ID (Fix #11, 2026-08-30). A UUID generated once per
 * browser tab, stashed in sessionStorage so it survives in-tab
 * reloads (like "tap to sign in" full-page reloads) but NOT across
 * new tabs. Every telemetry event auto-attaches this ID so log
 * analysis can trace a single user's complete journey.
 *
 * DISTINCTION from Fix #8's session_fingerprint:
 *   - This is CLIENT-generated (no trust required, correlation only)
 *   - Stored in sessionStorage (dies on tab close — intentional)
 *   - NOT stored in Mongo, NOT tied to user_id
 *   - Multiple concurrent IDs allowed (one per tab if user opens
 *     multiple tabs)
 * When Fix #8 ships, its `session_fingerprint` should be attached as
 * a SEPARATE metadata field, not merged with this token. See
 * /app/memory/DEFERRED_FIXES.md Fix #11 for the full rationale.
 */
const SESSION_ID_KEY = 'karaoke_client_session_id';
let _clientSessionId = null;
function getClientSessionId() {
  if (_clientSessionId) return _clientSessionId;
  try {
    _clientSessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!_clientSessionId) {
      // crypto.randomUUID is available in all modern browsers we
      // target (Safari 15.4+, Chrome 92+). Fall back to a
      // timestamp-based ID if unavailable (older Android WebView
      // pre-Chromium-92). Not cryptographically strong — just needs
      // to be unique enough for log correlation.
      _clientSessionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `t${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_ID_KEY, _clientSessionId);
    }
  } catch (_e) {
    // sessionStorage may throw in some Private Browsing modes. Fall
    // back to an in-memory ID that lasts for this page load.
    _clientSessionId = `mem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return _clientSessionId;
}

/**
 * POST a client debug event. `metadata` should be a small JSON-serializable
 * object (backend truncates any string field over 500 chars).
 *
 * Auto-attaches `client_session_id` to every event's metadata (Fix #11).
 *
 * Uses navigator.sendBeacon when available so the event still fires
 * even during unload / navigation. Falls back to fetch keepalive.
 */
export function reportClientEvent(eventType, metadata = {}) {
  if (!ALLOWED_EVENTS.has(eventType)) {
    // Silent no-op on unknown event types — keeps the backend
    // whitelist as the single source of truth without spamming
    // client-side console errors.
    return;
  }

  // Merge client_session_id into every payload for correlation.
  // Placed here (single choke point) rather than every call site so
  // future emit sites automatically inherit it.
  const enrichedMetadata = {
    ...(metadata || {}),
    client_session_id: getClientSessionId(),
  };

  const payload = JSON.stringify({
    event_type: eventType,
    metadata: enrichedMetadata,
  });
  const url = `${BACKEND_URL}/api/debug/client-event`;

  try {
    if (typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
      // sendBeacon can return false when the queue is full; fall through
      // to fetch.
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => { /* swallow — telemetry is best-effort */ });
  } catch (_e) {
    // Never let telemetry throw.
  }
}
